import { createContext, useContext, useRef, useCallback } from 'react';
import { isAriaTruthy } from '../utils/aria.js';
import { take } from '../utils/ref.js';
import { useInteractionHandler } from './use-interaction-handler.js';
import { useMacrotask } from './use-macrotask.js';

const ForceActionOnClickContext = createContext(false);
function useClickable({ onClick: onClickIn, recordingKey, actionOnPointerDown, onAuxClick: onAuxClickIn, onDoubleClick: onDoubleClickIn, onPointerDown: onPointerDownIn, onMouseDown: onMouseDownIn, onContextMenu: onContextMenuIn, disabled: disabledIn, 'aria-disabled': ariaDisabled, 
// ensure buttons are focusable even on safari
tabIndex = 0, ...props }) {
    // Within some contexts, like a Window, we always want to trigger the action on click.
    // This makes the Window's drag deadZone more effective since a user can pointerdown and drag the
    // Window from anywhere without worrying about triggering the action under the cursor.
    const forceActionOnClick = useContext(ForceActionOnClickContext);
    // Toggle tips can sometimes be on disabled buttons and we want people to still be able to
    // interact with them to say why they are disabled.
    const suppressClicks = (disabledIn || isAriaTruthy(ariaDisabled)) && !props['data-fpl-toggle-tip-override'];
    const deferredPointerEvent = useRef(null);
    const pointerDownForClick = useRef(null);
    // We need to fire the action after the browser has focused the element in order to allow
    // `:focus-visible` to work properly. Focus happens after the pointerdown event, so we defer the
    // action to a macrotask to allow that to happen. Its `pending` flag doubles as the signal that a
    // click fired on the same task as the pointerdown, which only happens for synthetic events (tests,
    // or AT clicks): if the click fires while it's still pending it was synthetic, otherwise it was a
    // real pointerdown->pointerup->click sequence.
    const deferredAction = useMacrotask();
    const recordedOnAction = useInteractionHandler((e) => {
        if (!suppressClicks)
            onClickIn?.(e);
    }, { eventName: 'click', recordingKey: onClickIn && !suppressClicks ? recordingKey : undefined }, [onClickIn, suppressClicks]);
    const onPointerDown = useCallback((e) => {
        if (!suppressClicks)
            onPointerDownIn?.(e);
    }, [onPointerDownIn, suppressClicks]);
    const onActionPointerDown = useCallback((e) => {
        onPointerDown(e);
        if (suppressClicks)
            return;
        pointerDownForClick.current = {
            pointerId: e.pointerId,
            pointerType: e.pointerType,
        };
        // on touch devices onPointerDown interferes with scrolling and triggers two click events
        if (e.button !== 0 || e.pointerType !== 'mouse')
            return;
        // Since we defer the action, a caller's e.stopPropagation() on the (by then stale) event
        // wouldn't take effect, so we always stopPropagation here — the main thing to stop is
        // dragging events in parent elements.
        e.stopPropagation();
        deferredPointerEvent.current = e;
        const currentTarget = e.currentTarget;
        deferredAction.schedule(() => {
            // React clears `currentTarget` after dispatch, so reapply both event targets before
            // forwarding the stored event.
            const pointerEvent = take(deferredPointerEvent);
            if (!pointerEvent || suppressClicks)
                return;
            recordedOnAction?.(Object.assign(pointerEvent, { target: currentTarget, currentTarget }));
        });
    }, [deferredAction, onPointerDown, recordedOnAction, suppressClicks]);
    // Menu uses onMouseDown to toggle menu containers. We need to intercept
    // it here to suppress it if the disabled prop is true (we're only setting
    // `aria-disabled`, not `disabled`, so we need to manually suppress clicks).
    const onMouseDown = useInteractionHandler((e) => {
        if (!suppressClicks)
            onMouseDownIn?.(e);
    }, {
        eventName: 'pointerdown',
        recordingKey: onMouseDownIn && !suppressClicks ? recordingKey : undefined,
    }, [onMouseDownIn, suppressClicks]);
    const onClick = useCallback((e) => {
        // If our deferred pointerdown action is pending, this click landed in the same event-loop as
        // the pointerdown — a virtual sequence (a test, or an AT that emits pointerdown->click
        // together). Fire here and cancel the pending action so we don't double fire.
        if (deferredAction.pending) {
            deferredAction.cancel();
            deferredPointerEvent.current = null;
            pointerDownForClick.current = null;
            recordedOnAction?.(e);
            return;
        }
        // No pending deferred action. Suppress a trailing click only when it matches a mouse
        // pointerdown whose action we already scheduled. Safari on iPad reports the click after a
        // touch pointerdown as pointerType "mouse", but preserves its pointerId, so the
        // preceding pointerdown type is what distinguishes that touch click from a real mouse click.
        // Keyboard, screen-reader, and programmatic clicks either have no matching pointerdown or
        // detail 0 and must still fire.
        if (!actionOnPointerDown ||
            forceActionOnClick ||
            !areBothMouseEventsFromSamePointer(e, take(pointerDownForClick))) {
            recordedOnAction?.(e);
        }
    }, [actionOnPointerDown, deferredAction, forceActionOnClick, recordedOnAction]);
    const onDoubleClick = useInteractionHandler((e) => {
        if (!suppressClicks)
            onDoubleClickIn?.(e);
    }, {
        eventName: 'dblclick',
        recordingKey: onDoubleClickIn && !suppressClicks ? recordingKey : undefined,
    }, [onDoubleClickIn, suppressClicks]);
    const onAuxClick = useInteractionHandler((e) => {
        if (!suppressClicks)
            onAuxClickIn?.(e);
    }, {
        eventName: 'auxclick',
        recordingKey: onAuxClickIn && !suppressClicks ? recordingKey : undefined,
    }, [onAuxClickIn, suppressClicks]);
    const onContextMenu = useInteractionHandler((e) => {
        if (!suppressClicks)
            onContextMenuIn?.(e);
    }, {
        eventName: 'contextmenu',
        recordingKey: onContextMenuIn && !suppressClicks ? recordingKey : undefined,
    }, [onContextMenuIn, suppressClicks]);
    const outProps = {
        'aria-disabled': disabledIn || ariaDisabled,
        // Forms still rely on type="submit" to submit, which doesn’t respect
        // [aria-disabled] by default. Apply the disabled prop in that one scenario.
        disabled: (disabledIn && props.type === 'submit') || undefined,
        onPointerDown: forceActionOnClick || !actionOnPointerDown ? onPointerDown : onActionPointerDown,
    };
    return {
        ...props,
        ...outProps,
        onMouseDown,
        onClick,
        onDoubleClick,
        onAuxClick,
        onContextMenu,
        tabIndex,
        // TODO: left this in case more data-show-focus guards are added while this is merging,
        // it should be removed in the future
        'data-show-focus': true,
    };
}
/** Returns whether a click and preceding pointerdown came from the same mouse pointer. */
function areBothMouseEventsFromSamePointer(event, pointerDown) {
    // Browser-generated mouse clicks have a positive click count. Keyboard and programmatic clicks
    // have detail 0 and must still activate.
    if (event.detail === 0)
        return false;
    if (pointerDown?.pointerType !== 'mouse')
        return false;
    const { nativeEvent } = event;
    if (!('pointerId' in nativeEvent) || !('pointerType' in nativeEvent))
        return false;
    return nativeEvent.pointerType === 'mouse' && nativeEvent.pointerId === pointerDown.pointerId;
}

export { ForceActionOnClickContext, useClickable };
//# sourceMappingURL=use-clickable.js.map
